import { X, Info, BookOpen, Layers, ClipboardCheck, MessageSquare, AlertTriangle, ShieldCheck } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

export default function TextbookModal({
  isOpen,
  onClose,
  onSave,
  textbook,
  formData,
  setFormData,
}) {
  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  // Safe Guard: If modal is open but formData hasn't populated yet, don't crash
  if (!formData) return null;

  // Dynamically compute the client-side structural live count summary tracker safely
  const computedTotal = 
    (Number(formData?.quantity_new) || 0) + 
    (Number(formData?.quantity_good) || 0) + 
    (Number(formData?.quantity_bad) || 0) + 
    (Number(formData?.quantity_lost) || 0);

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[150] flex items-center justify-center p-4 overflow-y-auto">
          {/* Ambient Backdrop Blur Overlay */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-slate-950/40 backdrop-blur-[6px]"
          />

          {/* Premium Floating Structural Surface Card */}
          <motion.div
            initial={{ scale: 0.98, opacity: 0, y: 8 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.98, opacity: 0, y: 8 }}
            transition={{ type: "spring", duration: 0.3, bounce: 0 }}
            className="bg-white rounded-xl w-full max-w-md shadow-2xl border border-slate-100 relative z-10 my-auto flex flex-col overflow-visible"
          >
            <div className="absolute top-0 left-6 right-6 h-[2px] bg-gradient-to-r from-transparent via-[#002855]/30 to-transparent" />

            {/* Modal Header Module */}
            <div className="px-6 py-5 border-b border-slate-100 flex items-center justify-between bg-slate-50/50 rounded-t-2xl">
              <div>
                <h2 className="text-sm font-semibold text-slate-900 tracking-tight flex items-center gap-2">
                  <BookOpen size={16} className="text-[#002855]" />
                  {textbook ? "Modify Textbook Parameters" : "Allocate New Textbook Asset"}
                </h2>
                <p className="text-[11px] text-slate-500 mt-0.5">
                  Register structural curriculum allocations into storage nodes.
                </p>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="p-1.5 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-200/50 transition-colors cursor-pointer"
              >
                <X size={15} />
              </button>
            </div>

            {/* Modal Body / Entry Schema Fields */}
            <form onSubmit={handleSubmit} className="p-6 space-y-4 text-xs font-medium text-slate-600">
              
              {/* Title/Description Input */}
              <div className="space-y-1.5">
                <label className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 flex items-center gap-1">
                  Resource Description / Title <span className="text-rose-500">*</span>
                </label>
                <input
                  type="text"
                  placeholder="e.g., Mathematics Grade 10 Core Textbook"
                  value={formData.description || ""}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-3 py-2 bg-slate-50/50 border border-slate-200 rounded-xl font-medium text-slate-800 placeholder-slate-400 outline-none focus:ring-2 focus:ring-[#002855]/10 focus:border-[#002855] focus:bg-white transition-all"
                  required
                />
              </div>

              {/* 2x2 Grid Layout Module for Condition Splits */}
              <div className="grid grid-cols-2 gap-3">
                
                {/* Quantity New Input */}
                <div className="space-y-1.5">
                  <label className="text-[10px] font-semibold uppercase tracking-wider text-blue-600 flex items-center gap-1">
                    <ShieldCheck size={12} className="text-blue-500" />
                    Brand New Units
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={formData.quantity_new ?? 0}
                    onChange={(e) => setFormData({ ...formData, quantity_new: parseInt(e.target.value) || 0 })}
                    className="w-full px-3 py-1.5 bg-slate-50/50 border border-slate-200 rounded-xl font-medium text-slate-800 outline-none focus:ring-2 focus:ring-blue-500/10 focus:border-blue-500 focus:bg-white transition-all"
                    required
                  />
                </div>

                {/* Quantity Good Input */}
                <div className="space-y-1.5">
                  <label className="text-[10px] font-semibold uppercase tracking-wider text-emerald-600 flex items-center gap-1">
                    <Layers size={12} className="text-emerald-500" />
                    Good / Usable Units
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={formData.quantity_good ?? 0}
                    onChange={(e) => setFormData({ ...formData, quantity_good: parseInt(e.target.value) || 0 })}
                    className="w-full px-3 py-1.5 bg-slate-50/50 border border-slate-200 rounded-xl font-medium text-slate-800 outline-none focus:ring-2 focus:ring-emerald-500/10 focus:border-emerald-500 focus:bg-white transition-all"
                    required
                  />
                </div>

                {/* Quantity Bad Input */}
                <div className="space-y-1.5">
                  <label className="text-[10px] font-semibold uppercase tracking-wider text-amber-600 flex items-center gap-1">
                    <ClipboardCheck size={12} className="text-amber-500" />
                    Damaged / Poor Units
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={formData.quantity_bad ?? 0}
                    onChange={(e) => setFormData({ ...formData, quantity_bad: parseInt(e.target.value) || 0 })}
                    className="w-full px-3 py-1.5 bg-slate-50/50 border border-slate-200 rounded-xl font-medium text-slate-800 outline-none focus:ring-2 focus:ring-amber-500/10 focus:border-amber-500 focus:bg-white transition-all"
                    required
                  />
                </div>

                {/* Quantity Lost Input */}
                <div className="space-y-1.5">
                  <label className="text-[10px] font-semibold uppercase tracking-wider text-rose-600 flex items-center gap-1">
                    <AlertTriangle size={12} className="text-rose-500" />
                    Missing / Lost Units
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={formData.quantity_lost ?? 0}
                    onChange={(e) => setFormData({ ...formData, quantity_lost: parseInt(e.target.value) || 0 })}
                    className="w-full px-3 py-1.5 bg-slate-50/50 border border-slate-200 rounded-xl font-medium text-slate-800 outline-none focus:ring-2 focus:ring-rose-500/10 focus:border-rose-500 focus:bg-white transition-all"
                    required
                  />
                </div>

              </div>

              {/* Remarks/Supplemental Notes Field */}
              <div className="space-y-1.5">
                <label className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 flex items-center gap-1">
                  <MessageSquare size={12} className="text-slate-400" />
                  Allocation Remarks & Ledger Notes
                </label>
                <textarea
                  placeholder="Document serial variations, missing bindings context, or assignment notes..."
                  value={formData.remarks || ""}
                  onChange={(e) => setFormData({ ...formData, remarks: e.target.value })}
                  rows={2}
                  className="w-full px-3 py-2 bg-slate-50/50 border border-slate-200 rounded-xl font-medium text-slate-800 placeholder-slate-400 outline-none focus:ring-2 focus:ring-[#002855]/10 focus:border-[#002855] focus:bg-white transition-all resize-none"
                />
              </div>

              {/* Operational Meta Row Actions Footer Controls */}
              <div className="flex items-center justify-between pt-3 border-t border-slate-100 mt-5">
                <div className="flex items-center gap-1.5 text-[10px] font-mono font-bold text-slate-400 uppercase tracking-wider">
                  <Info size={12} className="text-slate-300 shrink-0" />
                  Total: <span className="text-[#002855] bg-[#002855]/5 border border-[#002855]/10 px-1.5 py-0.5 rounded-md ml-1">{computedTotal} UNITS</span>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={onClose}
                    className="px-4 py-2 text-xs font-medium text-slate-600 hover:bg-slate-100 bg-white border border-slate-200 rounded-xl transition-all cursor-pointer"
                  >
                    Discard
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 text-xs font-medium text-white bg-[#002855] bg-opacity-80 hover:bg-opacity-95 rounded-xl shadow-xs transition-all cursor-pointer active:scale-98"
                  >
                    {textbook ? "Apply Modifications" : "Confirm Allocation"}
                  </button>
                </div>
              </div>

            </form>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}